import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/panel/modificarSaldosCXP.css";

const API_BASE = process.env.REACT_APP_API_URL || "";

const mspxFormatCRC = (n) => {
  const num = Number(n || 0);
  return (
    "¢" +
    num.toLocaleString("es-CR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

const mspxSafeText = (v, fallback = "-") => {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  return s.length ? s : fallback;
};

const ModificarSaldosCXP = () => {
  const navigate = useNavigate();

  // ✅ Guard
  useEffect(() => {
    const t = localStorage.getItem("token");
    if (!t) navigate("/login");
  }, [navigate]);

  const token = useMemo(() => localStorage.getItem("token"), []);
  const headers = useMemo(() => {
    const h = { "Content-Type": "application/json" };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);

  const [mspxLoading, setMspxLoading] = useState(false);
  const [mspxList, setMspxList] = useState([]);
  const [mspxSearch, setMspxSearch] = useState("");
  const [mspxMsg, setMspxMsg] = useState(null); // {type:'ok'|'err', text:''}

  // modal
  const [mspxEditOpen, setMspxEditOpen] = useState(false);
  const [mspxEditLoading, setMspxEditLoading] = useState(false);
  const [mspxSelected, setMspxSelected] = useState(null);
  const [mspxNewBal, setMspxNewBal] = useState("");

  const mspxFetch = async () => {
    setMspxMsg(null);
    setMspxLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/suppliers`, {
        method: "GET",
        headers,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Error cargando proveedores.");

      // tu backend normalmente devuelve array directo
      const arr = Array.isArray(data) ? data : data?.suppliers || [];
      setMspxList(arr);
    } catch (e) {
      setMspxMsg({ type: "err", text: e.message || "Error cargando proveedores." });
      setMspxList([]);
    } finally {
      setMspxLoading(false);
    }
  };

  useEffect(() => {
    mspxFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape para cerrar modal
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMspxEditOpen(false);
    };
    if (mspxEditOpen) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mspxEditOpen]);

  const mspxFiltered = useMemo(() => {
    const q = mspxSearch.trim().toLowerCase();
    if (!q) return mspxList;
    return mspxList.filter((s) => String(s?.name || "").toLowerCase().includes(q));
  }, [mspxList, mspxSearch]);

  const mspxOpenEdit = (supplier) => {
    setMspxMsg(null);
    setMspxSelected(supplier);
    setMspxNewBal(String(Number(supplier?.outstandingBalance || 0)));
    setMspxEditOpen(true);
  };

  const mspxSave = async () => {
    if (!mspxSelected) return;

    const supplierId = mspxSelected?.id;
    const newVal = Number(mspxNewBal);

    if (!Number.isFinite(newVal) || newVal < 0) {
      setMspxMsg({ type: "err", text: "Monto inválido. Debe ser >= 0." });
      return;
    }

    setMspxEditLoading(true);
    setMspxMsg(null);

    try {
      const res = await fetch(`${API_BASE}/api/suppliers/${supplierId}/outstanding-balance`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ outstandingBalance: newVal }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Error actualizando saldo.");

      setMspxList((prev) =>
        prev.map((x) => (String(x.id) === String(supplierId) ? { ...x, outstandingBalance: newVal } : x))
      );

      setMspxEditOpen(false);
      setMspxSelected(null);
      setMspxMsg({ type: "ok", text: "Saldo actualizado correctamente." });
    } catch (e) {
      setMspxMsg({ type: "err", text: e.message || "Error actualizando saldo." });
    } finally {
      setMspxEditLoading(false);
    }
  };

  return (
    <div className="mspx-wrap">
      <header className="mspx-head">
        <h1 className="mspx-title">Modificar Saldos (CxP)</h1>
        <p className="mspx-sub">
          Ajusta manualmente el saldo pendiente del proveedor (no afecta el módulo normal).
        </p>
      </header>

      <div className="mspx-bar">
        <input
          className="mspx-input"
          placeholder="Buscar proveedor..."
          value={mspxSearch}
          onChange={(e) => setMspxSearch(e.target.value)}
        />
        <button
          className="mspx-btn mspx-btn--primary"
          onClick={mspxFetch}
          disabled={mspxLoading}
          type="button"
        >
          {mspxLoading ? "Cargando..." : "Refrescar"}
        </button>
      </div>

      {mspxMsg?.text && (
        <div className={mspxMsg.type === "ok" ? "mspx-msg mspx-msg--ok" : "mspx-msg mspx-msg--err"}>
          {mspxMsg.text}
        </div>
      )}

      <div className="mspx-tableWrap">
        <table className="mspx-table">
          <thead>
            <tr>
              <th>Proveedor</th>
              <th>Saldo pendiente</th>
              <th>ID</th>
            </tr>
          </thead>

          <tbody>
            {mspxFiltered.length === 0 ? (
              <tr>
                <td colSpan="3" className="mspx-empty">
                  {mspxLoading ? "Cargando..." : "Sin resultados"}
                </td>
              </tr>
            ) : (
              mspxFiltered.map((s) => (
                <tr key={s.id}>
                  <td>{mspxSafeText(s.name)}</td>
                  <td>
                    <button
                      className="mspx-balanceBtn"
                      type="button"
                      onClick={() => mspxOpenEdit(s)}
                      title="Click para modificar saldo"
                    >
                      {mspxFormatCRC(s.outstandingBalance)}
                    </button>
                  </td>
                  <td>{s.id}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {mspxEditOpen && (
        <div className="mspx-modalOverlay" onClick={() => setMspxEditOpen(false)}>
          <div className="mspx-modalCard" onClick={(e) => e.stopPropagation()}>
            <button className="mspx-modalClose" onClick={() => setMspxEditOpen(false)} aria-label="Cerrar">
              ×
            </button>

            <h2 className="mspx-modalTitle">Modificar saldo pendiente</h2>

            <div className="mspx-modalInfo">
              <div>
                <b>Proveedor:</b> {mspxSafeText(mspxSelected?.name)}
              </div>
              <div>
                <b>Debe actualmente:</b> {mspxFormatCRC(mspxSelected?.outstandingBalance)}
              </div>
              <div>
                <b>ID:</b> {mspxSelected?.id}
              </div>
            </div>

            <div className="mspx-modalField">
              <label>Nuevo saldo pendiente</label>
              <input
                type="number"
                step="0.01"
                value={mspxNewBal}
                onChange={(e) => setMspxNewBal(e.target.value)}
              />
            </div>

            <div className="mspx-modalActions">
              <button className="mspx-btn mspx-btn--ghost" type="button" onClick={() => setMspxEditOpen(false)}>
                Cancelar
              </button>

              <button
                className="mspx-btn mspx-btn--primary"
                type="button"
                onClick={mspxSave}
                disabled={mspxEditLoading}
              >
                {mspxEditLoading ? "Guardando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModificarSaldosCXP;
